/**
 * @fileoverview The share studio's preview under the finger: a flick goes to
 * the next or previous picture, a drag moves the photograph in its frame, a
 * two-finger pinch zooms it (bug B10: the preview ignored a pinch, and a
 * second finger restarted the drag), a tap opens Move & zoom for it (in a
 * collage, the tile tapped) or the finished picture at full size. Moved out of
 * plant-list/components/dialogs/SharePlantDialog (batch 18, A105).
 *
 * Where: SharePlantDialog's preview, and the Crop band (Move & zoom).
 * Settings: SHARE_SWIPE_MS, SHARE_SWIPE_PX, SHARE_TAP_SLOP_PX, SHARE_ZOOM_MAX (config/tunables/plantList).
 *
 * @module usePreviewGestures
 */

import React from 'react';
import { collageTiles } from '@/features/share/frames/frameLayouts';
import { SHARE_SWIPE_MS, SHARE_SWIPE_PX, SHARE_TAP_SLOP_PX, SHARE_ZOOM_MAX } from '@/config/tunables/plantList';
import type { PlantPhoto } from '@/shared/types/photo';
import type { Editing, Focus } from './shareStudioTypes';
import type { ShareStory } from './useShareStory';

/** What usePreviewGestures hands the dialog. */
export interface PreviewGestures {
  /** The preview's box. */
  previewRef: React.RefObject<HTMLDivElement>;
  /** How far the preview is drawn off its place while dragged, in pixels. */
  nudge: { dx: number; dy: number } | null;
  /** The finished picture is shown at full size. */
  enlarged: boolean;
  /** Shows or hides the full-size picture. */
  setEnlarged: (on: boolean) => void;
  /** The photograph open in Move & zoom. */
  editing: Editing | null;
  /** Opens or closes Move & zoom. */
  setEditing: (editing: Editing | null) => void;
  /** Whether a drag moves the crop. */
  canReposition: boolean;
  /** The picture in view's place among those going out. */
  shownIndex: number;
  /** Shows the picture before (-1) or after (1). */
  showStep: (step: number) => void;
  /** A press on the preview. */
  onPreviewDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  /** The pointer's move. */
  onPreviewMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  /** A finger lifts or the press is cancelled. */
  onPreviewUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  /**
   * A two-finger pinch under way: how much to draw the picture larger than it
   * stands, about the point the crop keeps (where the zoom is anchored), as
   * fractions of the frame. Null when no pinch.
   */
  pinch: { scale: number; ox: number; oy: number } | null;
}

/**
 * The preview's presses, the full-size picture and Move & zoom.
 *
 * @param isOpen - Whether the sheet is open; closing it puts the full-size picture away.
 * @param story - The story and the choices (useShareStory).
 * @param previewUrl - The finished picture in view, or null while there is none.
 * @param w - The chosen size's width in pixels.
 * @param h - Its height in pixels.
 * @param slotAspectOf - The shape of the slot a photograph fills in the size and layout chosen (useShareFrames): Move & zoom's box takes it, and a drag or pinch keeps the crop made in it (B86).
 * @returns The preview's ref and handlers, its nudge while dragged, the full-size and Move & zoom states, and the steps between pictures.
 */
export function usePreviewGestures(isOpen: boolean, story: ShareStory, previewUrl: string | null, w: number, h: number, slotAspectOf: (photo: PlantPhoto) => number): PreviewGestures {
  const { shownPhoto, shownIsCard, shownIsCollage, layout, set, collageIn, slides, setShown, focusIn, saveFocus } = story;

  /* -------------------------------------------------------- reposition */
  /** The preview's box, which a drag is measured against and captures the pointer. */
  const previewRef = React.useRef<HTMLDivElement>(null);
  /** The press under way on the preview: where it began, the crop then, how far it has moved, and when. */
  const drag = React.useRef<{ x: number; y: number; start: Focus | null; dx: number; dy: number; at: number } | null>(null);
  /** Fingers on the preview: where each is, for a drag (one) or a pinch (two). */
  const fingers = React.useRef(new Map<number, { x: number; y: number }>());
  /** The pinch under way: the fingers' distance and the zoom when it began, and the photograph it zooms. */
  const pinchRef = React.useRef<{ d: number; z: number; zNow: number; photo: PlantPhoto; start: Focus } | null>(null);
  /** The pinch as drawn: the scale over the picture as it stands, and the point it grows from. */
  const [pinch, setPinch] = React.useState<{ scale: number; ox: number; oy: number } | null>(null);
  /** How far the preview is drawn off its place while being dragged, in pixels. */
  const [nudge, setNudge] = React.useState<{ dx: number; dy: number } | null>(null);
  /** The finished picture is shown at full size over the sheet, to read its words. */
  const [enlarged, setEnlarged] = React.useState(false);
  /** The photograph open to move and zoom on a phone, and the shape of the frame (or collage tile) it fills. */
  const [editing, setEditing] = React.useState<Editing | null>(null);
  React.useEffect(() => { if (!isOpen) setEnlarged(false); }, [isOpen]);
  /** Whether a drag moves the crop: not in Before & after, which has two, nor on a season's card. */
  const canReposition = layout !== 'ba' && Boolean(shownPhoto) && !shownIsCard && !shownIsCollage;
  /**
   * The picture in view's place among those going out. Before & after's
   * first photograph has no slide of its own, and stands for the picture.
   */
  const shownIndex = (() => {
    const at = slides.findIndex((p) => p.photo_id === shownPhoto?.photo_id);
    if (at >= 0) return at;
    return layout === 'ba' && !shownIsCard ? Math.max(0, slides.length - 1) : 0;
  })();
  /**
   * Shows the picture before or after the one in view, among those going out.
   *
   * @param step - -1 for the one before, 1 for the one after.
   */
  const showStep = (step: number): void => {
    const next = slides[shownIndex + step];
    if (next) setShown(next.photo_id);
  };
  /**
   * Has the preview follow a pointer past its edge; a pointer already gone makes
   * setPointerCapture throw, and the gesture then goes on without the capture.
   *
   * @param pointerId - The pointer.
   */
  const capture = (pointerId: number): void => {
    try { previewRef.current?.setPointerCapture(pointerId); } catch { /* pointer gone */ }
  };
  /**
   * A press on the preview: a quick sideways flick goes to the next or the
   * previous picture (when more than one is going out); a slower drag moves
   * the photograph in its frame, as before; a tap opens the finished
   * picture full size (onPreviewUp).
   *
   * @param event - The press.
   */
  const onPreviewDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!shownPhoto || event.button !== 0) return;
    // A second finger on a photograph that can be moved starts a pinch, and
    // the drag the first finger began is dropped (so it cannot move, flick or tap).
    if (canReposition && fingers.current.size === 1 && !pinchRef.current && !fingers.current.has(event.pointerId)) {
      fingers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      capture(event.pointerId);
      const [a, b] = [...fingers.current.values()];
      const start = focusIn(shownPhoto, slotAspectOf(shownPhoto));
      drag.current = null;
      setNudge(null);
      pinchRef.current = { d: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), z: start.z ?? 1, zNow: start.z ?? 1, photo: shownPhoto, start };
      return;
    }
    if (fingers.current.size >= 1) return;
    fingers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (!canReposition && slides.length < 2 && !previewUrl) return;
    drag.current = { x: event.clientX, y: event.clientY, start: canReposition ? focusIn(shownPhoto, slotAspectOf(shownPhoto)) : null, dx: 0, dy: 0, at: performance.now() };
    capture(event.pointerId);
  };
  /**
   * Moves the preview with the pointer while dragging, before the frame is built again.
   *
   * @param event - The pointer's move.
   */
  const onPreviewMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (fingers.current.has(event.pointerId)) fingers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const p = pinchRef.current;
    if (p) {
      // Two fingers: the zoom follows how far apart they are, between filling the frame and SHARE_ZOOM_MAX.
      if (fingers.current.size < 2) return;
      const [a, b] = [...fingers.current.values()];
      p.zNow = Math.min(SHARE_ZOOM_MAX, Math.max(1, p.z * (Math.hypot(a.x - b.x, a.y - b.y) / p.d)));
      setPinch({ scale: p.zNow / p.z, ox: p.start.x, oy: p.start.y });
      return;
    }
    if (!drag.current) return;
    drag.current.dx = event.clientX - drag.current.x;
    drag.current.dy = event.clientY - drag.current.y;
    setNudge({ dx: drag.current.dx, dy: drag.current.start ? drag.current.dy : 0 });
  };
  /**
   * Ends a press: a flick is a swipe, a real move the photograph's new place,
   * a tap opens the picture full size; a finger lifting from a pinch keeps the
   * zoom reached, and the finger left on the glass does nothing more.
   *
   * @param event - The finger lifting, or the press cancelled.
   */
  const onPreviewUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const wasDown = fingers.current.delete(event.pointerId);
    const p = pinchRef.current;
    if (p) {
      pinchRef.current = null;
      setPinch(null);
      const z = p.zNow < 1.005 ? 1 : p.zNow;
      if (z !== (p.start.z ?? 1)) saveFocus(p.photo, { ...p.start, z }, slotAspectOf(p.photo));
      return;
    }
    if (!wasDown) return;
    const d = drag.current;
    drag.current = null;
    setNudge(null);
    if (!d || !shownPhoto || !previewRef.current) return;
    const box = previewRef.current.getBoundingClientRect();
    // A tap: the photograph under the finger opens to move and zoom (in a
    // collage, the tile tapped); a picture with no photograph of its own to
    // move opens at full size.
    if (Math.abs(d.dx) < SHARE_TAP_SLOP_PX && Math.abs(d.dy) < SHARE_TAP_SLOP_PX) {
      if (shownIsCollage && collageIn) {
        const tiles = collageTiles(Math.min(9, set.length), w, h, w / 1080);
        const px = ((d.x - box.left) / box.width) * w;
        const py = ((d.y - box.top) / box.height) * h;
        const at = tiles.findIndex((tile) => px >= tile.x && px <= tile.x + tile.w && py >= tile.y && py <= tile.y + tile.h);
        const photo = at >= 0 ? set[at] : undefined;
        if (photo) {
          setEditing({ photo, aspect: tiles[at].w / tiles[at].h });
          return;
        }
      }
      if (canReposition) {
        setEditing({ photo: shownPhoto, aspect: slotAspectOf(shownPhoto) });
        return;
      }
      if (previewUrl) setEnlarged(true);
      return;
    }
    const flick = slides.length > 1 && Math.abs(d.dx) >= SHARE_SWIPE_PX && Math.abs(d.dx) > 2 * Math.abs(d.dy)
      && (performance.now() - d.at <= SHARE_SWIPE_MS || !d.start);
    if (flick) {
      showStep(d.dx < 0 ? 1 : -1);
      return;
    }
    if (!d.start) return;
    // Dragging the whole width of the preview moves the crop from one edge to the other.
    const next: Focus = {
      ...d.start,
      x: Math.min(1, Math.max(0, d.start.x - d.dx / box.width)),
      y: Math.min(1, Math.max(0, d.start.y - d.dy / box.height)),
    };
    saveFocus(shownPhoto, next, slotAspectOf(shownPhoto));
  };

  return {
    previewRef, nudge, enlarged, setEnlarged, editing, setEditing, canReposition, shownIndex, showStep,
    onPreviewDown, onPreviewMove, onPreviewUp, pinch,
  };
}
